import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { HerdCells, HerdMilking, HerdBacteria } from 'src/app/models/herd.enum';
import { PyramidPaths } from 'src/app/models/pyramid.enum';
import { ScrollRestorationService } from 'src/app/scroll-position-restoration.service';

@UntilDestroy()
@Component({
  selector: 'app-herd',
  templateUrl: './herd.component.html',
  styleUrls: ['./herd.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HerdComponent implements OnInit {
  form!: FormGroup;

  HerdCells = HerdCells;
  HerdMilking = HerdMilking;
  HerdBacteria = HerdBacteria;

  emergencyLink = ['/', PyramidPaths.Emergency];

  get showEmergency(): boolean {
    return this.form.get('cells')?.value === HerdCells.Above;
  }

  constructor(
    private fb: FormBuilder,
    private cd: ChangeDetectorRef,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private scrollRestorationService: ScrollRestorationService
  ) {}

  ngOnInit(): void {
    this.initForm();
  }

  onSubmitForm() {
    this.form.markAllAsTouched();

    if (this.form.valid) {
      let resultLink = null;

      const { milking, bacteria } = this.form.value;
      const { Conventional, Robot } = HerdMilking;
      const { Cow, Environmental, Unknown } = HerdBacteria;

      /** Decide with pyramid to be shown */
      if (Conventional === milking && Cow === bacteria) {
        resultLink = ['/', PyramidPaths.CowBacteria];
      } else if (Conventional === milking && Environmental === bacteria) {
        resultLink = ['/', PyramidPaths.EnvironmentBacteria];
      } else if (Robot === milking && Cow === bacteria) {
        resultLink = ['/', PyramidPaths.RobotCowBacteria];
      } else if (Robot === milking && Environmental === bacteria) {
        resultLink = ['/', PyramidPaths.RobotEnvironmentBacteria];
      } else if (Conventional === milking && Unknown === bacteria) {
        resultLink = ['/', PyramidPaths.CowBacteria];
      } else if (Robot === milking && Unknown === bacteria) {
        resultLink = ['/', PyramidPaths.RobotCowBacteria];
      }

      /** Redirect user to a pyramid */
      if (resultLink) {
        this.router.navigate(resultLink);
      }
    }
  }

  private initForm() {
    const { cells, milking, bacteria } = this.activatedRoute.snapshot.queryParams;

    /** Make sure to accept only valid values from query */
    const defaultCells = Object.values(HerdCells).find((value) => value === cells);
    const defaultMilking = Object.values(HerdMilking).find((value) => value === milking);
    const defaultBacteria = Object.values(HerdBacteria).find((value) => value === bacteria);

    this.form = this.fb.group({
      cells: [defaultCells, Validators.required],
      milking: [defaultMilking, Validators.required],
      bacteria: [defaultBacteria, Validators.required],
    });

    /** Store form values as URL query */
    this.form.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
      this.scrollRestorationService.prevent();
      this.router.navigate([], {
        replaceUrl: true,
        queryParams: { ...this.form.value },
        queryParamsHandling: 'merge',
      });
      this.cd.markForCheck();
    });
  }
}
